import * as React from 'react';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import {Box, CircularProgress, DialogTitle, Paper} from '@mui/material';
import {
	EntityType,
	EntryProperty,
	FormSelectOptions,
	PropertyDefinition,
	PropertyDefinitionBelongsTo,
	PropertyDefinitionHas,
	Repository,
	RepositoryEntry,
} from '../types';
import type {FormData as RepositoryEntryFormState} from '../types';
import {PROPERTY_DEFINITION_TYPES_TO_ENTRY_PROPERTY_TYPES} from '../constants';
import FormTemplate, { hideIdleInputUnderlineSx } from './FormTemplate';
import {assignRandomId, initFormData, isEntryTimestampPropertyDefinition, populateRepositoryEntriesForTable, populateSelectOptionsRepositoryEntries, sortEntryByRank} from '../common/util';
import { nextRepositoryEntryPrependRank } from '../common/fractionalRank';
import {entriesAddMany, selectAllEntryProperties, selectAllPropertyDefinitions, selectAllRepositoryEntries, selectEntryReadableValues, selectAllRepositories} from '../containers/contentSlice';
import {
	addRepositoryEntryDialogSetState,
	selectAddRepositoryEntryDialog,
} from '../containers/formDataSlice';
import {useAppDispatch, useAppSelector} from '../hooks';
import {selectIsAuthenticated} from '../containers/authSlice';
import {RepositoryEntriesRowDto, repositoryEntriesApi, useGetRepositoryByIdQuery, useGetRepositoryEntriesQuery, useRepositoryEntryCreateMutation} from '../services/repositoryEntries';
import dayjs from 'dayjs';
import useAuth from './Auth';
import RepositoryEntryPicker from './RepositoryEntryPicker';
import FloatingHintPill from './FloatingHintPill';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import { getCanvasCardSolidSurfaceBackground } from './CanvasCardFrame';

export interface OpenAddRepositoryEntryDialogProps {
	repositoryId?: string;
	linkToProperty?: PropertyDefinitionBelongsTo | PropertyDefinitionHas;
	linkToEntryId?: string;
}

export type {AddRepositoryEntryDialogState} from '../containers/formDataSlice';

export const useRepositoryEntryDialog = () => {
	const dispatch = useAppDispatch();
	const dialog = useAppSelector(selectAddRepositoryEntryDialog);
	const allRepositories = useAppSelector(selectAllRepositories);
	const isAuthenticated = useAppSelector(selectIsAuthenticated);

	const openAddRepositoryEntryDialog = useCallback(
		({repositoryId, linkToProperty, linkToEntryId}: OpenAddRepositoryEntryDialogProps = {}) => {
			if (!repositoryId) {
				return;
			}
			if (!isAuthenticated) {
				const repository = allRepositories.find((r) => r.id === repositoryId);
				if (!repository) {
					return;
				}
			}
			dispatch(addRepositoryEntryDialogSetState({repositoryId, linkToProperty, linkToEntryId}));
		},
		[allRepositories, isAuthenticated, dispatch],
	);

	const closeAddRepositoryEntryDialog = useCallback(() => {
		dispatch(addRepositoryEntryDialogSetState(null));
	}, [dispatch]);

	return {
		isAddRepositoryEntryDialogOpen: Boolean(dialog),
		addRepositoryEntryDialog: dialog,
		openAddRepositoryEntryDialog,
		closeAddRepositoryEntryDialog,
	};
};

export type AddFormData = Record<string, unknown>;

const populateEntryProps = (repositoryId: string): Partial<RepositoryEntry> => {
	return assignRandomId({
		type: EntityType.RepositoryEntry,
		parentId: repositoryId,
		repositoryId,
	});
};

const populateEntryPropertyData = (def: PropertyDefinition, entryId: string, value: any): EntryProperty => {
	const props = assignRandomId({
		type: PROPERTY_DEFINITION_TYPES_TO_ENTRY_PROPERTY_TYPES[def.type],
		repositoryId: def.parentId,
		propertyDefinitionId: def.id,
		slug: def.slug,
		parentId: entryId,
		value,
	});
	if (def.type === EntityType.PropertyDefinitionBelongsTo) {
		return {
			...props,
			targetRepositoryId: (def as PropertyDefinitionBelongsTo).targetRepositoryId,
		} as EntryProperty;
	}
	if (def.type === EntityType.PropertyDefinitionHas) {
		return assignRandomId({
			type: EntityType.EntryPropertyBelongsTo,
			repositoryId: (def as PropertyDefinitionHas).targetRepositoryId,
			// propertyDefinitionId: (def as PropertyDefinitionHas),
			targetRepositoryId: (def as PropertyDefinitionHas).parentId,
			slug: (def as PropertyDefinitionHas).targetPropertySlug,
			parentId: value,
			value: entryId,
		}) as EntryProperty;
	}
	return props as EntryProperty;
};

/** BelongsTo / Has fields: `targetRepositoryId` → entries from that repo's `GET repositories/:id` `populatedRepositoryEntries`. */
function formSelectOptionsFromRemoteRelationEntries(
	propertyDefinitions: PropertyDefinition[],
	populatedEntriesByTargetRepositoryId: Record<string, RepositoryEntriesRowDto[]>,
): FormSelectOptions {
	return propertyDefinitions
		.filter((prop) =>
			[EntityType.PropertyDefinitionBelongsTo, EntityType.PropertyDefinitionHas].includes(prop.type),
		)
		.reduce<FormSelectOptions>((acc, def) => {
			const targetRepositoryId = (def as PropertyDefinitionBelongsTo | PropertyDefinitionHas).targetRepositoryId;
			const rows = populatedEntriesByTargetRepositoryId[targetRepositoryId] ?? [];
			const options = rows
				.filter((e) => !e.isArchived)
				.map((e) => ({
					label: (e.label ?? '').trim() || '(no label)',
					value: e.id,
				}));
			return {...acc, [def.slug]: options};
		}, {});
}

export interface AddRepositoryEntryFormDialogProps {
	open: boolean;
	repositoryId: string | null;
	linkToProperty?: PropertyDefinitionBelongsTo | PropertyDefinitionHas;
	linkToEntryId?: string;
	onClose: () => void;
	/** Optional title override while loading; once `GET repositories/:id` returns, the title defaults to "Add {singular}". */
	title?: string;
	/** Pre-fills the new entry's form fields (keyed by property slug) — e.g. a date the user clicked before opening this dialog. */
	initialValues?: Record<string, unknown>;
	onCreated?: (entry: RepositoryEntry) => void;
	/** Adds a "Pick from existing" step that swaps the form for a picker of existing entries. */
	entryPicker?: {
		isEntryPickable: (row: RepositoryEntriesRowDto) => boolean;
		/** Text of the bottom alert, describing the `count` entries that can be picked (including none). */
		describePickableCount: (count: number, repositoryTitle: string) => string;
		/** Subtitle under the picker title. */
		hint: string;
		/** Text of the picker's confirm button for `count` selected entries. */
		confirmLabel: (count: number) => string;
		onConfirm: (selectedRepositoryEntryIds: string[]) => void;
	};
	elementSelectSlot?: React.ReactNode;
}

const STEP_TRANSITION = 'transform 450ms cubic-bezier(0.76, 0, 0.24, 1), visibility 450ms';

export const AddRepositoryEntryFormDialog: React.FC<AddRepositoryEntryFormDialogProps> = ({
	open,
	repositoryId,
	onClose,
	title,
	initialValues,
	onCreated,
	entryPicker,
	elementSelectSlot,
}) => {
	const [step, setStep] = useState<'form' | 'picker'>('form');

	useEffect(() => {
		if (open) {
			setStep('form');
		}
	}, [open]);

	const reduxDispatch = useAppDispatch();
	const isAuthenticatedRedux = useAppSelector(selectIsAuthenticated);
	const allRepositoryEntries = useAppSelector(selectAllRepositoryEntries);
	const allRepositories = useAppSelector(selectAllRepositories);
	const allPropertyDefinitions = useAppSelector(selectAllPropertyDefinitions);
	const allEntryProperties = useAppSelector(selectAllEntryProperties);
	const entryReadableValues = useAppSelector(selectEntryReadableValues);
	const {isAuthenticating} = useAuth();
	const isAuthenticated = isAuthenticatedRedux;
	const [repositoryEntryCreateMutation] = useRepositoryEntryCreateMutation();
	const {data: repositoryByIdResponse, isFetching: isMainRepositoryByIdFetching} = useGetRepositoryByIdQuery(repositoryId as string, {
		skip: !open || !repositoryId || !isAuthenticated || isAuthenticating,
	});
	const {data: repositoryEntriesResponse, isFetching: isMainRepositoryEntriesFetching} = useGetRepositoryEntriesQuery(repositoryId as string, {
		skip: !open || !repositoryId || !isAuthenticated || isAuthenticating,
	});

	const offlineRepository = useMemo((): Repository | null => {
		if (!repositoryId) {
			return null;
		}
		const found = allRepositories.find((r) => r.id === repositoryId);
		return found ?? null;
	}, [allRepositories, repositoryId]);

	const repository = useMemo((): Repository | null => {
		if (isAuthenticated) {
			return repositoryByIdResponse?.repository ?? null;
		}
		return offlineRepository;
	}, [isAuthenticated, repositoryByIdResponse?.repository, offlineRepository]);

	const sourcePropertyDefinitions = useMemo((): PropertyDefinition[] => {
		if (isAuthenticated) {
			const raw = repositoryByIdResponse?.propertyDefinitions ?? [];
			return [...raw].sort(sortEntryByRank);
		}
		if (!repositoryId) {
			return [];
		}
		return allPropertyDefinitions.filter((def) => def.parentId === repositoryId).sort(sortEntryByRank);
	}, [isAuthenticated, repositoryByIdResponse?.propertyDefinitions, repositoryId, allPropertyDefinitions]);

	const editablePropertyDefinitions = useMemo(
		() =>
			sourcePropertyDefinitions.filter(
				(def) =>
					!isAuthenticated || !isEntryTimestampPropertyDefinition(def),
			),
		[sourcePropertyDefinitions, isAuthenticated],
	);

	const formTemplateProperties = useMemo(
		() =>
			editablePropertyDefinitions.filter(
				(def) =>
					def.required ||
					![EntityType.PropertyDefinitionBelongsTo, EntityType.PropertyDefinitionHas].includes(def.type),
			),
		[editablePropertyDefinitions],
	);

	const relationTargetRepositoryIds = useMemo(() => {
		const ids = new Set<string>();
		for (const def of formTemplateProperties) {
			if (
				def.type === EntityType.PropertyDefinitionBelongsTo ||
				def.type === EntityType.PropertyDefinitionHas
			) {
				ids.add((def as PropertyDefinitionBelongsTo | PropertyDefinitionHas).targetRepositoryId);
			}
		}
		return Array.from(ids).sort();
	}, [formTemplateProperties]);

	const relationTargetRepositoryIdsKey = relationTargetRepositoryIds.join('\0');

	const [remoteRelationPopulatedEntriesByTargetRepositoryId, setRemoteRelationPopulatedEntriesByTargetRepositoryId] =
		useState<Record<string, RepositoryEntriesRowDto[]>>({});
	const [remoteRelationTargetsFetchPending, setRemoteRelationTargetsFetchPending] = useState(false);

	useEffect(() => {
		if (!open || !isAuthenticated || isAuthenticating) {
			setRemoteRelationPopulatedEntriesByTargetRepositoryId({});
			setRemoteRelationTargetsFetchPending(false);
			return;
		}
		if (relationTargetRepositoryIds.length === 0) {
			setRemoteRelationPopulatedEntriesByTargetRepositoryId({});
			setRemoteRelationTargetsFetchPending(false);
			return;
		}

		const ids = [...relationTargetRepositoryIds];
		setRemoteRelationTargetsFetchPending(true);
		setRemoteRelationPopulatedEntriesByTargetRepositoryId({});

		let cancelled = false;
		void (async () => {
			const results = await Promise.allSettled(
				ids.map((targetRepositoryId) =>
					reduxDispatch(
						repositoryEntriesApi.endpoints.getRepositoryEntries.initiate(targetRepositoryId, {
							subscribe: false,
						}),
					).unwrap(),
				),
			);
			if (cancelled) {
				return;
			}
			const byTargetId: Record<string, RepositoryEntriesRowDto[]> = {};
			results.forEach((result, index) => {
				const targetRepositoryId = ids[index];
				byTargetId[targetRepositoryId] =
					result.status === 'fulfilled' ? (result.value.rows as RepositoryEntriesRowDto[]) : [];
			});
			setRemoteRelationPopulatedEntriesByTargetRepositoryId(byTargetId);
			setRemoteRelationTargetsFetchPending(false);
		})();

		return () => {
			cancelled = true;
		};
	}, [open, isAuthenticated, isAuthenticating, relationTargetRepositoryIdsKey, reduxDispatch]);

	const relationRemoteSelectsReady =
		!isAuthenticated ||
		relationTargetRepositoryIds.length === 0 ||
		(!remoteRelationTargetsFetchPending &&
			relationTargetRepositoryIds.every((id) => Object.hasOwn(remoteRelationPopulatedEntriesByTargetRepositoryId, id)));

	const selectOptions = useMemo((): FormSelectOptions => {
		if (isAuthenticated) {
			return formSelectOptionsFromRemoteRelationEntries(
				formTemplateProperties,
				remoteRelationPopulatedEntriesByTargetRepositoryId,
			);
		}
		return populateSelectOptionsRepositoryEntries(formTemplateProperties, allRepositoryEntries, entryReadableValues);
	}, [isAuthenticated, formTemplateProperties, remoteRelationPopulatedEntriesByTargetRepositoryId, allRepositoryEntries, entryReadableValues]);

	const [formData, setFormData] = useState<RepositoryEntryFormState>({});
	const [dialogTitle, setDialogTitle] = useState('');
	const initSignatureRef = useRef<string | null>(null);
	const openCycleRef = useRef(0);
	const prevOpenRef2 = useRef(open);

	const isDataLayerReady =
		Boolean(repositoryId) &&
		(isAuthenticated ? Boolean(repositoryByIdResponse) && relationRemoteSelectsReady : Boolean(offlineRepository));

	useEffect(() => {
		if (open) {
			setDialogTitle(title ?? '');
		}
	}, [open, title]);

	useEffect(() => {
		if (repository) {
			setDialogTitle(`Create new ${repository.singular ?? repository.title}`);
		}
	}, [repository]);

	useEffect(() => {
		const wasOpen = prevOpenRef2.current;
		prevOpenRef2.current = open;
		if (!wasOpen && open) {
			openCycleRef.current += 1;
			initSignatureRef.current = null;
		}
	}, [open]);

	useEffect(() => {
		if (!open || !isDataLayerReady || editablePropertyDefinitions.length === 0 || !repositoryId) {
			return;
		}
		const signature = `${openCycleRef.current}:${repositoryId}:${editablePropertyDefinitions.length}:${relationTargetRepositoryIdsKey}`;
		if (initSignatureRef.current === signature) {
			return;
		}
		initSignatureRef.current = signature;
		const blankFormData = initFormData(editablePropertyDefinitions, initialValues);
		setFormData(blankFormData);
	}, [open, isDataLayerReady, repositoryId, editablePropertyDefinitions, relationTargetRepositoryIdsKey, initialValues]);

	const handleValueChange = useCallback((key: string, value: unknown) => {
		if (value !== ' ') {
			setFormData((prev) => ({...prev, [key]: value}));
		}
	}, []);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!repositoryId || !repository) {
			return;
		}
		const payload = Object.fromEntries(new FormData(e.target as HTMLFormElement).entries()) as AddFormData;
		const {...propertyValues} = payload;

		const existingSameRepository = (() => {
			if (isAuthenticated && repositoryEntriesResponse?.rows) {
				return repositoryEntriesResponse.rows
					.filter((row) => !row.isArchived)
					.map((row) => ({ id: row.id, rank: row.rank }));
			}
			if (!repositoryId) {
				return [];
			}
			return allRepositoryEntries
				.filter(
					(e) =>
						e.type === EntityType.RepositoryEntry &&
						e.repositoryId === repositoryId &&
						!(e as RepositoryEntry).isArchived,
				)
				.map((e) => ({ id: e.id, rank: (e as RepositoryEntry).rank }));
		})();

		const { rank } = nextRepositoryEntryPrependRank(existingSameRepository);

		const entry = {
			...populateEntryProps(repositoryId),
			rank,
		};
		const entryProperties: EntryProperty[] = formTemplateProperties
			.filter((def) => def.type !== EntityType.PropertyDefinitionHas)
			.reduce((acc: EntryProperty[], def) => {
				if (def.type === EntityType.PropertyDefinitionBoolean) {
					propertyValues[def.slug] = propertyValues[def.slug] === 'on' ? true : false;
					return [...acc, populateEntryPropertyData(def, entry.id as string, propertyValues[def.slug])];
				}
				if (def.type === EntityType.PropertyDefinitionBelongsTo) {
					const values = String(propertyValues[def.slug] ?? '').split(',');
					const belongsToValues = values.filter(Boolean).map((value: string) =>
						populateEntryPropertyData(def, entry.id as string, value),
					);
					return [...acc, ...belongsToValues];
				}
				if (def.type === EntityType.PropertyDefinitionDate) {
					const value = propertyValues[def.slug];
					const isValidDate = value && dayjs(value as string).isValid();
					return isValidDate
						? [...acc, populateEntryPropertyData(def, entry.id as string, dayjs(value as string).format('YYYY-MM-DD'))]
						: acc;
				}
				if (def.type === EntityType.PropertyDefinitionDatetime) {
					const value = propertyValues[def.slug];
					const isValidDatetime = value && dayjs(value as string).isValid();
					return isValidDatetime
						? [...acc, populateEntryPropertyData(def, entry.id as string, dayjs(value as string).toISOString())]
						: acc;
				}
				const value = propertyValues[def.slug];
				return value ? [...acc, populateEntryPropertyData(def, entry.id as string, value)] : acc;
			}, []);

		if (isAuthenticated) {
			try {
				const createdRecords = await repositoryEntryCreateMutation({
					entry,
					properties: entryProperties,
				}).unwrap();
				const createdEntry = (createdRecords as any[])?.find(
					(r: any) => r?.type === EntityType.RepositoryEntry && r?.fakeId === (entry as any).fakeId,
				);
				onCreated?.((createdEntry ?? entry) as RepositoryEntry);
			} catch {
				// ignore, error handling is global
			}
		} else {
			reduxDispatch(entriesAddMany([entry, ...entryProperties]));
			onCreated?.(entry as RepositoryEntry);
		}
		onClose();
	};

	const pickableCount = useMemo(() => {
		if (!entryPicker) return 0;
		let rows: RepositoryEntriesRowDto[] = [];
		if (isAuthenticated) {
			rows = repositoryEntriesResponse?.rows ?? [];
		} else if (offlineRepository) {
			rows = populateRepositoryEntriesForTable(offlineRepository, allRepositoryEntries, allPropertyDefinitions, allEntryProperties);
		}
		return rows.filter((row) => !row.isArchived && entryPicker.isEntryPickable(row)).length;
	}, [allEntryProperties, allPropertyDefinitions, allRepositoryEntries, entryPicker, isAuthenticated, offlineRepository, repositoryEntriesResponse?.rows]);

	const showLoading =
		open &&
		Boolean(repositoryId) &&
		(isAuthenticated
			? isMainRepositoryByIdFetching || !relationRemoteSelectsReady || isMainRepositoryEntriesFetching
			: !offlineRepository);

	return (
		<Dialog
			open={open}
			onClose={onClose}
			fullScreen
			slotProps={{
				paper: {
					sx: {
						backgroundColor: 'transparent',
						backgroundImage: 'none',
						backdropFilter: 'none',
						WebkitBackdropFilter: 'none',
						boxShadow: 'none',
						overflow: 'hidden',
					},
				},
			}}
		>
			<Box
				aria-hidden={step !== 'form'}
				sx={{
					position: 'absolute',
					inset: 0,
					transform: step === 'form' ? 'none' : 'translateX(100%)',
					visibility: step === 'form' ? 'visible' : 'hidden',
					transition: STEP_TRANSITION,
				}}
			>
				<Box sx={{ height: '100%', overflowY: 'auto' }}>
					<Box
						display="flex"
						alignItems="center"
						justifyContent="center"
						minHeight="100%"
						p={4}
						pb={entryPicker ? 12 : 4}
						onClick={(e) => {
							if (e.target === e.currentTarget) onClose();
						}}
					>
						<Paper
							elevation={24}
							sx={(theme) => ({
								width: '100%',
								maxWidth: 444,
								backgroundColor: getCanvasCardSolidSurfaceBackground(theme, { collectionColor: repository?.color ?? undefined }),
							})}
						>
							<Box display="flex" justifyContent="space-between" alignItems="center">
								<DialogTitle>{dialogTitle}</DialogTitle>
								<Box pr={2}>{elementSelectSlot}</Box>
							</Box>
							<form onSubmit={handleSubmit} autoComplete="off">
								<DialogContent sx={hideIdleInputUnderlineSx}>
									{showLoading ? (
										<Box display="flex" justifyContent="center" py={3}>
											<CircularProgress size={32} />
										</Box>
									) : (
										<FormTemplate
											propertyDefinitions={formTemplateProperties}
											formData={formData}
											onValueChange={handleValueChange}
											selectOptions={selectOptions}
											textFieldVariant="standard"
										/>
									)}
								</DialogContent>
								<DialogActions>
									<Button onClick={onClose}>Cancel</Button>
									<Button type="submit" variant="contained" disabled={showLoading}>
										Save
									</Button>
								</DialogActions>
							</form>
						</Paper>
					</Box>
				</Box>
				{entryPicker && !showLoading && (
					<FloatingHintPill
						onClick={pickableCount ? () => setStep('picker') : undefined}
						startIcon={pickableCount ? <MenuOpenIcon fontSize="small" /> : <PlaylistAddCheckIcon fontSize="small" />}
					>
						{entryPicker.describePickableCount(pickableCount, repository?.title ?? '')}
					</FloatingHintPill>
				)}
			</Box>
			{entryPicker && (
				<Box
					aria-hidden={step !== 'picker'}
					sx={{
						position: 'absolute',
						inset: 0,
						transform: step === 'picker' ? 'none' : 'translateX(-100%)',
						visibility: step === 'picker' ? 'visible' : 'hidden',
						transition: STEP_TRANSITION,
					}}
				>
					<RepositoryEntryPicker
						active={open && step === 'picker'}
						repositoryId={repositoryId}
						isEntryPickable={entryPicker.isEntryPickable}
						hint={entryPicker.hint}
						confirmLabel={entryPicker.confirmLabel}
						onClose={onClose}
						onConfirm={(selectedRepositoryEntryIds) => {
							entryPicker.onConfirm(selectedRepositoryEntryIds);
							onClose();
						}}
						onCreateNewClick={() => setStep('form')}
					/>
				</Box>
			)}
		</Dialog>
	);
};
